import { useState, useCallback, useEffect, useRef } from 'react';
import { ReasonCode } from '@/services/api/flightChanges';
import { useTranslation } from '@/hooks/i18n/useTranslation';
import {
  ChangeReasonData,
  CaseStatusAction,
  ChangeCodeFormData,
  SavedFormValues,
  CASE_STATUS_ACTIONS,
  NOT_APPLICABLE_REASONS,
  NotApplicableReasonId,
} from './ChangeCodeForm.types';

/** Acciones que persisten datos y requieren el bloque de motivo (RC Croamis) completo. */
const ACTIONS_REQUIRING_REASON_FIELDS = new Set<CaseStatusAction>([
  CASE_STATUS_ACTIONS.SAVE_ONLY,
  CASE_STATUS_ACTIONS.ON_HOLD,
  CASE_STATUS_ACTIONS.NOT_APPLICABLE,
  CASE_STATUS_ACTIONS.SEND_NOTIFICATION,
]);

interface UseChangeCodeFormProps {
  changeReason: ChangeReasonData;
  reasonCodes: ReasonCode[];
  onSave: (data: ChangeCodeFormData) => void | Promise<void>;
  savedValues?: SavedFormValues | null;
}

const parseSelectValue = (value: string): number | null => {
  if (!value) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

export const useChangeCodeForm = ({ changeReason, reasonCodes, onSave, savedValues }: UseChangeCodeFormProps) => {
  const { t } = useTranslation();
  const [reasonCodeCorrect, setReasonCodeCorrect] = useState<'yes' | 'no' | null>(null);
  const [selectedReasonCodeId, setSelectedReasonCodeId] = useState<number | null>(null);
  const [selectedRemarkId, setSelectedRemarkId] = useState<number | null>(null);
  const [remarkError, setRemarkError] = useState<boolean>(false);
  
  const [selectedInitialRemarkId, setSelectedInitialRemarkId] = useState<number | null>(null);
  const [initialRemarkError, setInitialRemarkError] = useState<boolean>(false);
  
  const [reasonCodeCorrectError, setReasonCodeCorrectError] = useState<boolean>(false);
  const [newReasonCodeError, setNewReasonCodeError] = useState<boolean>(false);
  const [showValidationAlert, setShowValidationAlert] = useState<boolean>(false);
  const [showNoChangesToSaveAlert, setShowNoChangesToSaveAlert] = useState<boolean>(false);

  const [selectedAction, setSelectedAction] = useState<CaseStatusAction | null>(null);
  const [actionError, setActionError] = useState<boolean>(false);
  
  const [observations, setObservations] = useState<string>('');
  const [isNewRc, setIsNewRc] = useState<boolean | null>(null);
  

  const [showNotApplicableModal, setShowNotApplicableModal] = useState(false);
  const [showSendNotificationModal, setShowSendNotificationModal] = useState(false);
  const [showNonNotifiableRCModal, setShowNonNotifiableRCModal] = useState(false);
  const [isSubmittingNotApplicable, setIsSubmittingNotApplicable] = useState(false);
  const [isSubmittingSendNotification, setIsSubmittingSendNotification] = useState(false);
  const [isSubmittingNonNotifiableWarning, setIsSubmittingNonNotifiableWarning] = useState(false);
  const [isSubmittingDirectSave, setIsSubmittingDirectSave] = useState(false);
  /** Refs block a second confirm before React applies `isSubmitting` (double-click race). */
  const notApplicableSubmitLockRef = useRef(false);
  const sendNotificationSubmitLockRef = useRef(false);
  const nonNotifiableWarningSubmitLockRef = useRef(false);
  const directSaveSubmitLockRef = useRef(false);

  useEffect(() => {
    if (savedValues) {
      const { changeReason: savedChangeReason, notes } = savedValues;
      const isKeepingSame = savedChangeReason.isInitialReasonCodeCorrect === true;
      
      setReasonCodeCorrect(isKeepingSame ? 'yes' : 'no');
      
      if (isKeepingSame) {
        setSelectedInitialRemarkId(savedChangeReason.initialRemarkId);
        setSelectedReasonCodeId(null);
        setSelectedRemarkId(null);
      } else {
        setSelectedInitialRemarkId(null);
        setSelectedReasonCodeId(savedChangeReason.newReasonCodeId);
        setSelectedRemarkId(savedChangeReason.newRemarkId);
      }
      
      setObservations(notes || '');
      setSelectedAction(null);
      setReasonCodeCorrectError(false);
      setInitialRemarkError(false);
      setNewReasonCodeError(false);
      setRemarkError(false);
      setActionError(false);
      setShowValidationAlert(false);
      setShowNoChangesToSaveAlert(false);
    }
  }, [savedValues]);

  const selectedReasonCode = reasonCodes.find(rc => rc.id === selectedReasonCodeId);
  const isRemarkRequired = selectedReasonCode?.isRemarkApplicable ?? false;
  const availableRemarks = selectedReasonCode?.remarks ?? [];


  const checkIsReasonCodeNotifiable = useCallback((): boolean => {
    if (reasonCodeCorrect === 'yes') {
      const initialRC = reasonCodes.find(rc => rc.id === changeReason.initialReasonCodeId);
      if (!initialRC) return false;

      if (changeReason.isInitialRemarkRequired && selectedInitialRemarkId) {
        const selectedRemark = initialRC.remarks?.find(r => r.id === selectedInitialRemarkId);
        return selectedRemark?.isNotifiable ?? initialRC.isNotifiable;
      }

      return initialRC.isNotifiable;
    }

    if (reasonCodeCorrect === 'no' && selectedReasonCodeId) {
      const newRC = reasonCodes.find(rc => rc.id === selectedReasonCodeId);
      if (!newRC) return false;

      if (isRemarkRequired && selectedRemarkId) {
        const selectedRemark = newRC.remarks?.find(r => r.id === selectedRemarkId);
        return selectedRemark?.isNotifiable ?? newRC.isNotifiable;
      }

      return newRC.isNotifiable;
    }

    return false;
  }, [
    reasonCodeCorrect,
    reasonCodes,
    changeReason.initialReasonCodeId,
    changeReason.isInitialRemarkRequired,
    selectedInitialRemarkId,
    selectedReasonCodeId,
    selectedRemarkId,
    isRemarkRequired,
  ]);

  const hasDataChanges = useCallback((): boolean => {
    if (savedValues) {
      const { changeReason: savedChangeReason, notes: savedNotes } = savedValues;
      const savedIsKeepingSame = savedChangeReason.isInitialReasonCodeCorrect === true;
      const savedReasonCodeCorrect = savedIsKeepingSame ? 'yes' : 'no';
      
      if (reasonCodeCorrect !== savedReasonCodeCorrect) return true;
      
      if (observations !== (savedNotes || '')) return true;
      
      if (savedIsKeepingSame) {
        return selectedInitialRemarkId !== savedChangeReason.initialRemarkId;
      }
      return (
        selectedReasonCodeId !== savedChangeReason.newReasonCodeId ||
        selectedRemarkId !== savedChangeReason.newRemarkId
      );
    }
    
    if (reasonCodeCorrect !== null) return true;
    if (selectedInitialRemarkId !== null) return true;
    if (selectedReasonCodeId !== null) return true;
    if (selectedRemarkId !== null) return true;
    if (observations.trim() !== '') return true;
    
    return false;
  }, [reasonCodeCorrect, selectedInitialRemarkId, selectedReasonCodeId, selectedRemarkId, observations, savedValues]);

  const hasChanges = useCallback((): boolean => {
    if (hasDataChanges()) return true;
    if (selectedAction !== null) return true;
    return false;
  }, [hasDataChanges, selectedAction]);

  const handleReasonCodeChange = (value: 'yes' | 'no') => {
    setReasonCodeCorrect(value);
    setReasonCodeCorrectError(false);
    setInitialRemarkError(false);
    setRemarkError(false);
    setNewReasonCodeError(false);
    setShowValidationAlert(false);
    setShowNoChangesToSaveAlert(false);
  };

  const createRemarkChangeHandler = (
    setRemarkId: React.Dispatch<React.SetStateAction<number | null>>,
    setError: React.Dispatch<React.SetStateAction<boolean>>
  ) => (raw: string) => {
    setRemarkId(parseSelectValue(raw));
    setError(false);
    setShowValidationAlert(false);
    setShowNoChangesToSaveAlert(false);
  };

  const handleNewReasonCodeChange = (raw: string) => {
    setSelectedReasonCodeId(parseSelectValue(raw));
    setSelectedRemarkId(null);
    setRemarkError(false);
    setNewReasonCodeError(false);
    setShowValidationAlert(false);
    setShowNoChangesToSaveAlert(false);
  };

  const handleNewRemarkChange = createRemarkChangeHandler(setSelectedRemarkId, setRemarkError);
  const handleInitialRemarkChange = createRemarkChangeHandler(setSelectedInitialRemarkId, setInitialRemarkError);

  const handleActionChange = (raw: string) => {
    const value = raw as CaseStatusAction;
    setSelectedAction(value || null);
    setActionError(false);
    setShowValidationAlert(false);
    setShowNoChangesToSaveAlert(false);
  };

  const handleObservationsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setObservations(e.target.value);
    setShowNoChangesToSaveAlert(false);
  };

  const persistDirectSave = async (formData: ChangeCodeFormData) => {
    if (directSaveSubmitLockRef.current) return;
    directSaveSubmitLockRef.current = true;
    setIsSubmittingDirectSave(true);
    try {
      await onSave(formData);
    } catch {
    } finally {
      directSaveSubmitLockRef.current = false;
      setIsSubmittingDirectSave(false);
    }
  };

  const handleSaveWithValidation = (): void => {
    if (directSaveSubmitLockRef.current) {
      return;
    }

    setShowNoChangesToSaveAlert(false);

    let hasErrors = false;

    if (!selectedAction) {
      setActionError(true);
      hasErrors = true;
    }

    const needsReasonFields =
      selectedAction != null && ACTIONS_REQUIRING_REASON_FIELDS.has(selectedAction);

    if (needsReasonFields) {
      if (reasonCodeCorrect === null) {
        setReasonCodeCorrectError(true);
        hasErrors = true;
      }

      if (changeReason.isInitialRemarkRequired && reasonCodeCorrect !== 'no') {
        const isValidInitialRemark =
          selectedInitialRemarkId !== null &&
          changeReason.remarkOptions.some((r) => r.id === selectedInitialRemarkId);

        if (!isValidInitialRemark) {
          setInitialRemarkError(true);
          hasErrors = true;
        }
      }

      if (reasonCodeCorrect === 'no') {
        if (!selectedReasonCodeId) {
          setNewReasonCodeError(true);
          hasErrors = true;
        }
        if (isRemarkRequired && !selectedRemarkId) {
          setRemarkError(true);
          hasErrors = true;
        }
      }
    }

    if (hasErrors) {
      setShowValidationAlert(true);
      return;
    }

    if (selectedAction === CASE_STATUS_ACTIONS.SAVE_ONLY && !hasDataChanges()) {
      setShowNoChangesToSaveAlert(true);
      return;
    }

    if (selectedAction === CASE_STATUS_ACTIONS.NOT_APPLICABLE) {
      setShowNotApplicableModal(true);
      return;
    }

    if (selectedAction === CASE_STATUS_ACTIONS.SEND_NOTIFICATION) {
      const isNotifiable = checkIsReasonCodeNotifiable();

      if (!isNotifiable) {
        setShowNonNotifiableRCModal(true);
        return;
      }

      setShowSendNotificationModal(true);
      return;
    }

    const isKeepingSameRC = reasonCodeCorrect === 'yes';

    const formData: ChangeCodeFormData = {
      action: selectedAction!,
      notes: observations || null,
      changeReason: {
        initialReasonCodeId: changeReason.initialReasonCodeId,
        isInitialReasonCodeCorrect: isKeepingSameRC,
        initialRemarkId: isKeepingSameRC ? (selectedInitialRemarkId ?? null) : null,
        newReasonCodeId: isKeepingSameRC ? null : (selectedReasonCodeId ?? null),
        newRemarkId: isKeepingSameRC ? null : (selectedRemarkId ?? null),
      },
    };

    void persistDirectSave(formData);
  };

  const handleConfirmNotApplicable = async (reasonId: string) => {
    if (notApplicableSubmitLockRef.current) return;
    notApplicableSubmitLockRef.current = true;

    const labelKey = NOT_APPLICABLE_REASONS[reasonId as NotApplicableReasonId];
    const reasonNotApplicable = labelKey ? t(labelKey) : reasonId;

    const isKeepingSameRC = reasonCodeCorrect === 'yes';

    const formData: ChangeCodeFormData = {
      action: CASE_STATUS_ACTIONS.NOT_APPLICABLE,
      notes: observations || null,
      reasonNotApplicable,
      changeReason: {
        initialReasonCodeId: changeReason.initialReasonCodeId,
        isInitialReasonCodeCorrect: isKeepingSameRC,
        initialRemarkId: isKeepingSameRC ? (selectedInitialRemarkId ?? null) : null,
        newReasonCodeId: isKeepingSameRC ? null : (selectedReasonCodeId ?? null),
        newRemarkId: isKeepingSameRC ? null : (selectedRemarkId ?? null),
      },
    };

    setIsSubmittingNotApplicable(true);
    try {
      await onSave(formData);
      setShowNotApplicableModal(false);
    } catch {
    } finally {
      notApplicableSubmitLockRef.current = false;
      setIsSubmittingNotApplicable(false);
    }
  };

  const handleCancelNotApplicable = () => {
    setShowNotApplicableModal(false);
  };

  const handleConfirmSendNotification = async () => {
    if (sendNotificationSubmitLockRef.current) return;
    sendNotificationSubmitLockRef.current = true;

    const isKeepingSameRC = reasonCodeCorrect === 'yes';

    const formData: ChangeCodeFormData = {
      action: CASE_STATUS_ACTIONS.SEND_NOTIFICATION,
      notes: observations || null,
      changeReason: {
        initialReasonCodeId: changeReason.initialReasonCodeId,
        isInitialReasonCodeCorrect: isKeepingSameRC,
        initialRemarkId: isKeepingSameRC ? (selectedInitialRemarkId ?? null) : null,
        newReasonCodeId: isKeepingSameRC ? null : (selectedReasonCodeId ?? null),
        newRemarkId: isKeepingSameRC ? null : (selectedRemarkId ?? null),
      },
    };

    setIsSubmittingSendNotification(true);
    try {
      await onSave(formData);
      setShowSendNotificationModal(false);
    } catch {
    } finally {
      sendNotificationSubmitLockRef.current = false;
      setIsSubmittingSendNotification(false);
    }
  };

  const handleCancelSendNotification = () => {
    setShowSendNotificationModal(false);
  };


  const handleConfirmNonNotifiableRC = async () => {
    if (nonNotifiableWarningSubmitLockRef.current) return;
    nonNotifiableWarningSubmitLockRef.current = true;

    const isKeepingSameRC = reasonCodeCorrect === 'yes';
    const reasonNotApplicable = t(NOT_APPLICABLE_REASONS.non_notifiable_rc);

    const formData: ChangeCodeFormData = {
      action: CASE_STATUS_ACTIONS.NOT_APPLICABLE,
      notes: observations || null,
      reasonNotApplicable,
      changeReason: {
        initialReasonCodeId: changeReason.initialReasonCodeId,
        isInitialReasonCodeCorrect: isKeepingSameRC,
        initialRemarkId: isKeepingSameRC ? (selectedInitialRemarkId ?? null) : null,
        newReasonCodeId: isKeepingSameRC ? null : (selectedReasonCodeId ?? null),
        newRemarkId: isKeepingSameRC ? null : (selectedRemarkId ?? null),
      },
    };

    setIsSubmittingNonNotifiableWarning(true);
    try {
      await onSave(formData);
      setShowNonNotifiableRCModal(false);
    } catch {
    } finally {
      nonNotifiableWarningSubmitLockRef.current = false;
      setIsSubmittingNonNotifiableWarning(false);
    }
  };

  const handleCancelNonNotifiableRC = () => {
    setShowNonNotifiableRCModal(false);
  };

  return {
    reasonCodeCorrect,
    reasonCodeCorrectError,
    selectedReasonCodeId,
    selectedRemarkId,
    remarkError,
    newReasonCodeError,
    selectedInitialRemarkId,
    initialRemarkError,
    showValidationAlert,
    showNoChangesToSaveAlert,
    isRemarkRequired,
    availableRemarks,
    selectedAction,
    actionError,
    observations,
    hasChanges,
    showNotApplicableModal,
    showSendNotificationModal,
    showNonNotifiableRCModal,
    isSubmittingNotApplicable,
    isSubmittingSendNotification,
    isSubmittingNonNotifiableWarning,
    isSubmittingDirectSave,
    handleReasonCodeChange,
    handleNewReasonCodeChange,
    handleNewRemarkChange,
    handleInitialRemarkChange,
    handleActionChange,
    handleObservationsChange,
    handleSaveWithValidation,
    handleConfirmNotApplicable,
    handleCancelNotApplicable,
    handleConfirmSendNotification,
    handleCancelSendNotification,
    handleConfirmNonNotifiableRC,
    handleCancelNonNotifiableRC,
    checkIsReasonCodeNotifiable,
  };
};

